/**
 * Data + pure-logic layer for `bulk-targeting` (FS-812).
 *
 * Mirrors the backend contract at `apps/api/src/routes/bulk-targeting.ts`:
 *   - POST /api/v1/bulk-targeting           { csvText } -> ImportResult
 *   - GET  /api/v1/bulk-targeting/jobs      -> { jobs: JobResponse[] }
 *   - GET  /api/v1/bulk-targeting/job/:id   -> { job: JobResponse }
 *
 * CSV schema (header required, order-insensitive):
 *   campaign_id, type, value, mode
 *
 * Targeting types:  geo | device | os | browser
 * Targeting modes:  allow | block
 *
 * All raw `fetch()` calls live in THIS file only (D6 contract). The page
 * component parses the CSV client-side for preview/validation, then POSTs
 * the raw text to the backend which re-parses authoritatively.
 */

export const TARGETING_TYPES = ["geo", "device", "os", "browser"] as const;
export type TargetingType = (typeof TARGETING_TYPES)[number];

export const TARGETING_MODES = ["allow", "block"] as const;
export type TargetingMode = (typeof TARGETING_MODES)[number];

export const MAX_CSV_ROWS = 5000;

const REQUIRED_COLUMNS = ["campaign_id", "type", "value", "mode"] as const;

const CAMPAIGN_ID_RE = /^[a-z0-9_-]{1,64}$/i;
const GEO_RE = /^[A-Za-z]{2}$/;

const DEVICE_TOKENS = ["mobile", "desktop", "tablet"] as const;
const OS_TOKENS = ["ios", "android", "windows", "macos", "linux", "chromeos"] as const;
const BROWSER_TOKENS = [
	"chrome",
	"safari",
	"firefox",
	"edge",
	"opera",
	"samsung",
	"ie",
] as const;

export interface ParsedRow {
	campaign_id: string;
	type: TargetingType;
	value: string;
	mode: TargetingMode;
}

export interface RowError {
	row: number; // 1-based after header
	error: string;
	raw: string;
}

export interface ParseResult {
	rows: ParsedRow[];
	errors: RowError[];
	headerOk: boolean;
	truncated: boolean;
}

export interface JobResponse {
	id: string;
	tenant_id: string;
	parsed: number;
	applied: number;
	error_count: number;
	errors: RowError[];
	created_at: number;
}

export interface ImportResult {
	jobId: string;
	parsed: number;
	applied: number;
	errors: RowError[];
	truncated: boolean;
	maxRows: number;
}

// ---------------------------------------------------------------------------
// CSV tokenizer — mirrors apps/api/src/lib/bulk-targeting.ts so the preview
// matches the backend authoritative parse exactly.
// ---------------------------------------------------------------------------

export function parseCsvLine(line: string): string[] {
	const out: string[] = [];
	let cur = "";
	let inQuotes = false;
	let i = 0;
	while (i < line.length) {
		const ch = line[i];
		if (inQuotes) {
			if (ch === '"') {
				if (line[i + 1] === '"') {
					cur += '"';
					i += 2;
					continue;
				}
				inQuotes = false;
				i += 1;
				continue;
			}
			cur += ch;
			i += 1;
			continue;
		}
		if (ch === '"') {
			inQuotes = true;
			i += 1;
			continue;
		}
		if (ch === ",") {
			out.push(cur);
			cur = "";
			i += 1;
			continue;
		}
		cur += ch;
		i += 1;
	}
	out.push(cur);
	return out;
}

export function splitCsvLines(input: string): string[] {
	const normalized = input.replace(/\r\n?/g, "\n");
	const lines = normalized.split("\n");
	if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
	return lines;
}

// ---------------------------------------------------------------------------
// Field validators
// ---------------------------------------------------------------------------

export function isValidCampaignId(value: unknown): value is string {
	return typeof value === "string" && CAMPAIGN_ID_RE.test(value);
}

export function isValidType(value: unknown): value is TargetingType {
	return (
		typeof value === "string" &&
		(TARGETING_TYPES as readonly string[]).includes(value.toLowerCase())
	);
}

export function isValidMode(value: unknown): value is TargetingMode {
	return (
		typeof value === "string" &&
		(TARGETING_MODES as readonly string[]).includes(value.toLowerCase())
	);
}

export function normalizeValue(
	type: TargetingType,
	raw: string,
): { ok: true; value: string } | { ok: false; error: string } {
	const trimmed = raw.trim();
	if (!trimmed) return { ok: false, error: "value_required" };
	if (trimmed.length > 64) return { ok: false, error: "value_too_long" };

	switch (type) {
		case "geo": {
			if (!GEO_RE.test(trimmed)) return { ok: false, error: "invalid_geo" };
			return { ok: true, value: trimmed.toUpperCase() };
		}
		case "device": {
			const lower = trimmed.toLowerCase();
			if (!(DEVICE_TOKENS as readonly string[]).includes(lower)) {
				return { ok: false, error: "invalid_device" };
			}
			return { ok: true, value: lower };
		}
		case "os": {
			const lower = trimmed.toLowerCase();
			if (!(OS_TOKENS as readonly string[]).includes(lower)) {
				return { ok: false, error: "invalid_os" };
			}
			return { ok: true, value: lower };
		}
		case "browser": {
			const lower = trimmed.toLowerCase();
			if (!(BROWSER_TOKENS as readonly string[]).includes(lower)) {
				return { ok: false, error: "invalid_browser" };
			}
			return { ok: true, value: lower };
		}
	}
}

// ---------------------------------------------------------------------------
// Parser — pure function, deterministic. Matches backend row-indexing
// (errors are 1-based relative to the header row).
// ---------------------------------------------------------------------------

export function parseCsv(text: string): ParseResult {
	if (typeof text !== "string" || text.trim().length === 0) {
		return { rows: [], errors: [], headerOk: false, truncated: false };
	}

	const lines = splitCsvLines(text);
	if (lines.length === 0) {
		return { rows: [], errors: [], headerOk: false, truncated: false };
	}

	const rawHeader = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
	const colIdx: Record<string, number> = {};
	for (const col of REQUIRED_COLUMNS) {
		const idx = rawHeader.indexOf(col);
		if (idx === -1) {
			return { rows: [], errors: [], headerOk: false, truncated: false };
		}
		colIdx[col] = idx;
	}

	const rows: ParsedRow[] = [];
	const errors: RowError[] = [];
	let truncated = false;

	const seen = new Set<string>();
	const dataCount = lines.length - 1;
	const limit = Math.min(dataCount, MAX_CSV_ROWS);
	if (dataCount > MAX_CSV_ROWS) truncated = true;

	for (let r = 0; r < limit; r++) {
		const raw = lines[r + 1];
		if (raw.trim() === "") continue;
		const cols = parseCsvLine(raw);

		if (cols.length !== rawHeader.length) {
			errors.push({ row: r + 1, error: "column_count_mismatch", raw });
			continue;
		}

		const campaignId = (cols[colIdx.campaign_id] ?? "").trim();
		if (!isValidCampaignId(campaignId)) {
			errors.push({ row: r + 1, error: "invalid_campaign_id", raw });
			continue;
		}

		const typeRaw = (cols[colIdx.type] ?? "").trim();
		if (!isValidType(typeRaw)) {
			errors.push({ row: r + 1, error: "invalid_type", raw });
			continue;
		}
		const type = typeRaw.toLowerCase() as TargetingType;

		const modeRaw = (cols[colIdx.mode] ?? "").trim();
		if (!isValidMode(modeRaw)) {
			errors.push({ row: r + 1, error: "invalid_mode", raw });
			continue;
		}
		const mode = modeRaw.toLowerCase() as TargetingMode;

		const valueRaw = cols[colIdx.value] ?? "";
		const valueResult = normalizeValue(type, valueRaw);
		if (!valueResult.ok) {
			errors.push({ row: r + 1, error: valueResult.error, raw });
			continue;
		}

		const key = `${campaignId}|${type}|${valueResult.value}|${mode}`;
		if (seen.has(key)) {
			errors.push({ row: r + 1, error: "duplicate_row", raw });
			continue;
		}
		seen.add(key);

		rows.push({
			campaign_id: campaignId,
			type,
			value: valueResult.value,
			mode,
		});
	}

	return { rows, errors, headerOk: true, truncated };
}

// ---------------------------------------------------------------------------
// Humanize an error code for inline display.
// ---------------------------------------------------------------------------

export function humanizeErrorCode(code: string): string {
	switch (code) {
		case "invalid_campaign_id":
			return "Campaign id must be 1-64 chars, a-z 0-9 _ or -.";
		case "invalid_type":
			return "Type must be one of: geo, device, os, browser.";
		case "invalid_mode":
			return "Mode must be allow or block.";
		case "invalid_geo":
			return "Geo must be a 2-letter ISO country code (e.g. US).";
		case "invalid_device":
			return "Device must be mobile, desktop, or tablet.";
		case "invalid_os":
			return "OS must be ios, android, windows, macos, linux, or chromeos.";
		case "invalid_browser":
			return "Browser must be chrome, safari, firefox, edge, opera, samsung, or ie.";
		case "value_required":
			return "Value is required.";
		case "value_too_long":
			return "Value may not exceed 64 characters.";
		case "column_count_mismatch":
			return "Column count does not match header.";
		case "duplicate_row":
			return "Duplicate of an earlier row.";
		default:
			return code;
	}
}

// ---------------------------------------------------------------------------
// Remote bindings — raw fetch ONLY lives here (D6 contract).
// ---------------------------------------------------------------------------

const BASE = "/api/v1/bulk-targeting";

export const SAMPLE_CSV = `campaign_id,type,value,mode
cmp_summer,geo,US,allow
cmp_summer,device,mobile,allow
cmp_summer,os,ios,allow
cmp_summer,browser,chrome,allow
cmp_winter,geo,CA,block`;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function importCsv(csvText: string): Promise<ImportResult> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ csvText }),
	});
	return parseOrThrow<ImportResult>(res);
}

export async function listJobs(): Promise<JobResponse[]> {
	const res = await fetch(`${BASE}/jobs`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const data = await parseOrThrow<{ jobs: JobResponse[] }>(res);
	return data.jobs;
}

export async function getJob(jobId: string): Promise<JobResponse> {
	const res = await fetch(`${BASE}/job/${encodeURIComponent(jobId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const data = await parseOrThrow<{ job: JobResponse }>(res);
	return data.job;
}

export const bulkTargetingQueryKeys = {
	all: ["bulk-targeting"] as const,
	list: () => [...bulkTargetingQueryKeys.all, "jobs"] as const,
	detail: (id: string) => [...bulkTargetingQueryKeys.all, id] as const,
};
